import React from 'react';
import i18n from 'util/i18n';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import Location from 'components/Form/Location';
import Branch from 'components/Form/Branch';
import Text from 'components/Form/Text';
import PVQLocation from 'components/Form/PVQLocation';
import { PsychologicalHospitalizationsItemModel } from 'forms/sections/psychological/hospitalizations/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import {
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
} from '../../../../types/props';

interface Props extends Partial<PsychologicalHospitalizationsItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
}

export default class HospitalizationItem extends ValidationElement<Props> {
  update = (queue: Partial<PsychologicalHospitalizationsItemModel>) => {
    this.props.onUpdate({
      Admission: this.props.Admission,
      TreatmentDate: this.props.TreatmentDate,
      Facility: this.props.Facility,
      FacilityAddress: this.props.FacilityAddress,
      Explanation: this.props.Explanation,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateTreatmentDate = (values: PsychologicalHospitalizationsItemModel['TreatmentDate']) => {
    this.update({
      TreatmentDate: values,
    });
  };

  updateAdmission = (values: PsychologicalHospitalizationsItemModel['Admission']) => {
    const sanitizedValues = values;
    if (isPVQForm(this.props.formType)) {
      if (sanitizedValues && !['Yes', 'No'].includes(sanitizedValues.value)) {
        sanitizedValues.value = '';
      }
    }
    this.update({
      Admission: sanitizedValues,
    });
  };

  updateFacility = (values: PsychologicalHospitalizationsItemModel['Facility']) => {
    this.update({
      Facility: values,
    });
  };

  updateFacilityAddress = (values: PsychologicalHospitalizationsItemModel['FacilityAddress']) => {
    this.update({
      FacilityAddress: values,
    });
  };

  updateExplanation = (values: PsychologicalHospitalizationsItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateComments = (values: PsychologicalHospitalizationsItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="hospitalization">
        <Field
          attribute={`${attribute}.Admission`}
          className="psychology-admission"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            attribute={`${attribute}.Admission`}
            name="admission"
            label={i18n.t('psychological.pvq.hospitalization.heading.admission')}
            labelSize="h4"
            className="psychology-admission"
            {...this.props.Admission}
            onUpdate={this.updateAdmission}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
        </Field>

        <Field
          title={i18n.t('psychological.pvq.hospitalization.heading.treatment')}
          attribute={`${attribute}.TreatmentDate`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="TreatmentDate"
            attribute={`${attribute}.TreatmentDate`}
            {...this.props.TreatmentDate}
            minDateEqualTo
            onUpdate={this.updateTreatmentDate}
            onError={this.props.onError}
            required={this.props.required}
            help="psychological.pvq.hospitalization.help.treatment"
            hideFromDay
            hideToDay
            usePvqFromDate
            usePvqToDate
            fromDateLabel={i18n.t('psychological.pvq.hospitalization.heading.treatmentStart')}
            toDateLabel={i18n.t('psychological.pvq.hospitalization.heading.treatmentEnd')}
            allowPresent={false}
          />
        </Field>

        <Field
          title={i18n.t('psychological.pvq.hospitalization.heading.facility')}
          attribute={`${attribute}.Facility`}
          exactAttribute
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Facility"
            attribute={`${attribute}.Facility`}
            className="facility"
            {...this.props.Facility}
            onUpdate={this.updateFacility}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          attribute={`${attribute}.FacilityAddress`}
          optional
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          dataTestId="Address"
        >
          <PVQLocation
            name="Address"
            attribute={`${attribute}.FacilityAddress`}
            {...this.props.FacilityAddress}
            label={i18n.t(`psychological.pvq.hospitalization.label.address`)}
            help="psychological.hospitalization.help.address"
            domesticTitle={i18n.t(`psychological.pvq.hospitalization.label.addressDomesticTitle`)}
            foreignTitle={i18n.t(`psychological.pvq.hospitalization.label.addressForeignTitle`)}
            layout={Location.ADDRESS}
            geocode
            addressBook="Treatment"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateFacilityAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showInternationalStreet
          />
        </Field>
      </div>
    ) : (
      <div className="hospitalization">
        <Field
          title={i18n.t('psychological.hospitalization.heading.admission')}
          attribute={`${attribute}.Admission`}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
        >
          <RadioGroup
            className="admission option-list optoin-list-vertical"
            name="admission"
            attribute={`${attribute}.Admission`}
            selectedValue={(this.props.Admission || {}).value}
            required={this.props.required}
            onError={this.props.onError}
          >
            <Radio
              className="voluntary-option"
              value="Voluntary"
              onError={this.props.onError}
              onUpdate={this.updateAdmission}
              label={i18n.t('psychological.hospitalization.label.voluntaryAdmission')}
            />
            <Radio
              className="involuntary-option"
              value="Involuntary"
              onError={this.props.onError}
              onUpdate={this.updateAdmission}
              label={i18n.t('psychological.hospitalization.label.involuntaryAdmission')}
            />
          </RadioGroup>
        </Field>

        <Field
          title={i18n.t('psychological.hospitalization.heading.explanation')}
          attribute={`${attribute}.Explanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Explanation"
            attribute={`${attribute}.Explanation`}
            className="explanation"
            {...this.props.Explanation}
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('psychological.hospitalization.heading.treatment')}
          attribute={`${attribute}.TreatmentDate`}
          help="psychological.hospitalization.help.treatment"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="TreatmentDate"
            attribute={`${attribute}.TreatmentDate`}
            {...this.props.TreatmentDate}
            minDateEqualTo
            onUpdate={this.updateTreatmentDate}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t('psychological.hospitalization.heading.facility')}
          attribute={`${attribute}.Facility`}
          exactAttribute
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Facility"
            attribute={`${attribute}.Facility`}
            className="facility"
            {...this.props.Facility}
            onUpdate={this.updateFacility}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('psychological.hospitalization.heading.address')}
          attribute={`${attribute}.FacilityAddress`}
          optional
          help="psychological.hospitalization.help.address"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            name="FacilityAddress"
            attribute={`${attribute}.FacilityAddress`}
            {...this.props.FacilityAddress}
            label={i18n.t('psychological.hospitalization.label.address')}
            layout={Location.ADDRESS}
            geocode
            showPostOffice
            onUpdate={this.updateFacilityAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
          />
        </Field>
        <Comments
          title={i18n.t('psychological.hospitalization.itemComments')}
          name="Comments"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

HospitalizationItem.defaultProps = {
  Admission: '',
  Comments: {},
  formType: '',
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
